import { useDebounce, useToggle } from 'ahooks';
import cx from 'classnames';
import FullScreenPlayer from 'components/molecules/FullScreenPlayer';
import Header from 'components/molecules/Header';
import { HomePageLoader } from 'components/molecules/Loaders';
// import NewYearNoticeBottomSheet from 'components/organisms/BottomSheets/NewYearNoticeBottomSheet';
import EidNoticeBottomSheet from 'components/organisms/BottomSheets/EidNoticeBottomSheet';
import DownloadAppBanner from 'components/organisms/DownloadAppBanner';
import OnboardingBottomSheet from 'components/organisms/OnboardingBottomSheet';
import OrderHistoryButtonRow from 'components/organisms/OrderHistoryButtonRow';
import RateOrderSheet from 'components/organisms/RateOrderSheet';
import { useConfig } from 'context/app-config';
import { SelectService } from 'packages/careem/home/components';
import HomeCarousel from 'packages/careem/home/components/HomeCarousel';
import HomeFooter from 'packages/shared/home/components/HomeFooter';
import PrimaryButtonIcon from 'packages/shared/home/components/HomeFooter/PrimaryButtonIcon';
import PricingSheet from 'packages/shared/pricing/PricingSheet';
import { selectSelectedServicesCount } from 'pages/newOrder/newOrder.selectors';
import { useGlobalConfig } from 'pages/queries/global.queries';
import { useCarousel } from 'pages/queries/home.queries';
import { useFetchCustomer } from 'pages/queries/user.queries';
import React, { useCallback, useMemo, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import { useSelector } from 'react-redux';
import { selectCustomer, selectOrderToRate, selectPartner } from 'redux-stores/slices/authSlice';
import { selectBannerImage, selectHomeVideo } from 'redux-stores/slices/uiSlice';
import { styled } from 'styled-components';
import { splitCommaSeparatedValues } from 'utils/helpers/extras';

const StyledContainer = styled(Container)`
  display: ${({ isLoading }) => (isLoading ? 'none' : 'block')};
  padding-bottom: 150px;
`;

const PrefetchHooks = () => {
  // update customer data coming on home page
  useFetchCustomer();

  return null;
};

const Home = () => {
  const { data: appConfig } = useGlobalConfig();
  const customer = useSelector(selectCustomer);
  const config = useConfig();
  const { data: carouselItems, isLoading: carouselLoading } = useCarousel(customer?.id, true);
  const isLoading = useDebounce(carouselLoading, { wait: 400 });
  const orderToRate = useSelector(selectOrderToRate);
  const [showRateOrderSheet, setShowRateOrderSheet] = useState(!!orderToRate);
  const [showPricingSheet, setShowPricingSheet] = useState(false);
  const [showOnboardingBottomSheet, { setRight, setLeft }] = useToggle(false);
  const selectedServicesCount = useSelector(selectSelectedServicesCount);
  const partnerId = useSelector(selectPartner);
  const hasSupportForMultipleDropoffs = useMemo(() => config?.modules.newOrder.dropoffs?.hasSupportForMultipleDropoffs, [config]);
  const alwaysShowCarouselForPartners = useMemo(() => splitCommaSeparatedValues(appConfig?.alwaysShowNewCarouselForPartners ?? ''), [appConfig]);
  const isSupportCarousel = useMemo(() => alwaysShowCarouselForPartners.includes(partnerId?.toLowerCase()), [alwaysShowCarouselForPartners]);
  const video = useSelector(selectHomeVideo);
  const toggleRateOrderSheet = useCallback((value: boolean) => setShowRateOrderSheet(value), []);
  const bannerImage = useSelector(selectBannerImage);

  const handleCloseOrderRatingSheet = useCallback(() => {
    toggleRateOrderSheet(false);
  }, [toggleRateOrderSheet]);

  const togglePricingSheet = useCallback((value: boolean) => setShowPricingSheet(value), []);

  const handleClosePricingSheet = useCallback(() => {
    togglePricingSheet(false);
  }, [togglePricingSheet]);

  const openPricingSheet = useCallback(() => {
    togglePricingSheet(true);
  }, [togglePricingSheet]);
  const Footer = useMemo(
    () => (
      <>
        <EidNoticeBottomSheet />
        <HomeFooter
          isVisible={selectedServicesCount > 0 || !hasSupportForMultipleDropoffs}
          primaryButtonIcon={<PrimaryButtonIcon />}
          hidePriceButton
        />
        <PricingSheet show={showPricingSheet} onClose={handleClosePricingSheet} />
        <RateOrderSheet orderId={orderToRate} show={showRateOrderSheet} onClose={handleCloseOrderRatingSheet} />
        <OnboardingBottomSheet show={showOnboardingBottomSheet} onClose={setLeft} />
      </>
    ),
    [
      handleCloseOrderRatingSheet,
      handleClosePricingSheet,
      hasSupportForMultipleDropoffs,
      orderToRate,
      selectedServicesCount,
      setLeft,
      showOnboardingBottomSheet,
      showPricingSheet,
      showRateOrderSheet,
    ]
  );

  const MainBannerCarousel = useMemo(
    () =>
      isSupportCarousel ? (
        <HomeCarousel className="mt-2" items={carouselItems} openPricingSheet={openPricingSheet} toggleOnboardingSheet={setRight} />
      ) : (
        <Row>
          <Col className="my-5">
            <FullScreenPlayer thumbnail={bannerImage} videoSrc={video} />
          </Col>
        </Row>
      ),
    [bannerImage, carouselItems, isSupportCarousel, openPricingSheet, setRight, video]
  );

  return (
    <>
      <StyledContainer isLoading={isLoading} className={cx('home-container', { 'mt-50': !config?.hideHeader })}>
        {!config?.hideHeader && (
          <Row>
            <Header isShowMenuOption isDisplayUserName />
          </Row>
        )}
        <DownloadAppBanner />
        {MainBannerCarousel}
        <OrderHistoryButtonRow />
        {hasSupportForMultipleDropoffs && <SelectService togglePricingSheet={openPricingSheet} />}
        {Footer}
      </StyledContainer>
      {isLoading && <HomePageLoader />}
      {/* <NewYearNoticeBottomSheet /> */}
      <PrefetchHooks />
    </>
  );
};

export default Home;
